import { inject } from '@angular/core'
import { patchState, signalStoreFeature, withHooks, withMethods, withState } from '@ngrx/signals'
import { Events, withEventHandlers } from '@ngrx/signals/events'
import { rxMethod } from '@ngrx/signals/rxjs-interop'
import { Actions, ofType } from '@ngrx/effects'
import { EMPTY, filter, finalize, map, pipe, switchMap, tap } from 'rxjs'

import { conversationEvents } from '@app/modules/messaging/components/store/conversation.events'
import { CONVERSATION_STORE_CONFIG } from '@app/modules/messaging/components/store/conversation.store'
import type { ConversationProfile } from '@app/modules/messaging/models/conversation-profile.model'
import { PopupService } from '@app/modules/popup/popup.service'
import { BidComponent } from '@app/modules/top-admirers/components/bid/bid.component'
import { TopAdmirersComponent } from '@app/modules/top-admirers/components/top-admirers/top-admirers.component'
import { BidCacheService } from '@app/modules/top-admirers/services/bid-cache.service'
import { BidService } from '@app/modules/top-admirers/services/bid.service'
import * as fromTopAdmirers from '@app/modules/top-admirers/store'
import { ExplainerService } from '@app/overlays/explainer/explainer.service'
import { ModalService } from '@app/overlays/modal/modal.service'
import { SiteStore } from '@app/store/site.store'
import type { Bid } from '@app/models/bid'
import type { PopupName } from '@app/modules/popup/popup.model'

interface State {
  topAdmirersLoading: boolean
  bidProfile: ConversationProfile | null
}

const initialState: State = {
  topAdmirersLoading: false,
  bidProfile: null,
}

export const topAdmirerFeature = () =>
  signalStoreFeature(
    withState(initialState),
    withMethods(
      (
        store,
        popupService = inject(PopupService),
        explainerService = inject(ExplainerService),
        modalService = inject(ModalService),
        bidService = inject(BidService),
        source = inject(CONVERSATION_STORE_CONFIG).source,
      ) => {
        const _startLoading = () => patchState(store, { topAdmirersLoading: true })
        const _stopLoading = () => patchState(store, { topAdmirersLoading: false })

        const openBidModal = (profile: ConversationProfile, bids: Bid[], bid?: number) => {
          patchState(store, { bidProfile: profile })

          const { instance } = modalService.create(BidComponent, { padded: false, desktopWidth: '600px' })

          if (bid) {
            instance.bid = bid
          }

          instance.bids = bids
          instance.profile = profile
          instance.source = 'messages-page'
        }

        const handleBids = (profile: ConversationProfile, bids: Bid[]) => {
          if (source !== 'messages-page') {
            return EMPTY
          }

          if (bids.length === 0) {
            openBidModal(profile, bids)
            return EMPTY
          }

          const { instance } = modalService.create(TopAdmirersComponent, {
            padded: true,
            desktopWidth: '600px',
          })
          instance.bids = bids
          instance.profile = profile
          instance.source = source
          instance.me = false

          return instance.placeBid.pipe(
            tap(() => {
              instance.close()
              openBidModal(profile, instance.bids)
            }),
          )
        }

        const openTopAdmirersModal = rxMethod<{ profile: ConversationProfile }>(
          pipe(
            tap(() => _startLoading()),
            switchMap(({ profile }) =>
              bidService.getBids(profile.browse_id).pipe(
                switchMap(bids => {
                  const result$ = handleBids(profile, bids)
                  _stopLoading()
                  return result$
                }),
              ),
            ),
          ),
        )

        const _openBidModal = rxMethod<{ profile: ConversationProfile; bid?: number }>(
          pipe(
            tap(() => _startLoading()),
            switchMap(({ profile, bid }) =>
              bidService.getBids(profile.browse_id).pipe(
                tap(bids => openBidModal(profile, bids, bid)),
                finalize(() => _stopLoading()),
              ),
            ),
          ),
        )

        const buildExplainer = (
          element: HTMLElement,
          profile: ConversationProfile,
          title: string,
          description: string,
          buttonCopy: string,
        ) => {
          const { instance, componentRef } = explainerService.show({
            element,
            radius: 50,
            title,
            description,
            backdropHighlightRadius: 4,
            buttonCopy,
            buttonVariant: 'fill-purple-gradient',
          })

          instance.onConfirm(() => openTopAdmirersModal({ profile }))

          return { componentRef }
        }

        const openTopAdmirerExplainer = (
          key: PopupName,
          element: HTMLElement,
          profile: ConversationProfile,
          title: string,
          description: string,
          buttonCopy: string,
        ) => popupService.askForShowing(key, () => buildExplainer(element, profile, title, description, buttonCopy))

        const openTopAdmirerIntro = (topAdmirerButton: HTMLElement, profile: ConversationProfile) =>
          openTopAdmirerExplainer(
            'message-top-admirer-intro',
            topAdmirerButton,
            profile,
            'Need her to reply?',
            `Show ${profile.username} you're worth her time by becoming a Top Admirer.`,
            'Become a Top Admirer',
          )

        const openTopAdmirerOutbidExplainer = (topAdmirerButton: HTMLElement, profile: ConversationProfile) =>
          openTopAdmirerExplainer(
            'message-top-admirer-outbid',
            topAdmirerButton,
            profile,
            'You’ve been outbid',
            `View ${profile.username}'s updated leaderboard and take back your spot!`,
            'View Leaderboard',
          )

        return { openTopAdmirerIntro, openTopAdmirerOutbidExplainer, openTopAdmirersModal, _openBidModal }
      },
    ),
    withEventHandlers(
      (
        store,
        events = inject(Events),
        actions$ = inject(Actions),
        bidCacheService = inject(BidCacheService),
        siteStore = inject(SiteStore),
        source = inject(CONVERSATION_STORE_CONFIG).source,
      ) => ({
        restoreCachedBid$: events.on(conversationEvents.loadSuccess).pipe(
          filter(() => !!siteStore.features().top_admirer),
          map(({ payload }) => payload.response.conversation.profile),
          tap(profile => {
            const { bid } = bidCacheService.getCachedBid(profile.browse_id)

            if (bid && source === 'messages-page') {
              store._openBidModal({ profile, bid })
            }
          }),
        ),
        reopenTopAdmirers$: actions$.pipe(
          ofType(fromTopAdmirers.Actions.bidSuccess),
          tap(({ profileId }) => {
            const profile = store.bidProfile()

            if (source === 'messages-page' && profile?.id === profileId) {
              store.openTopAdmirersModal({ profile })
            }
          }),
        ),
      }),
    ),
    withHooks({
      onInit: (_store, bidCacheService = inject(BidCacheService)) => {
        void bidCacheService.init()
      },
    }),
  )
